import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  getMonth,
  getYear,
  isSameDay,
  setMonth as setMonthDate,
  setYear as setYearDate,
  startOfMonth,
  subMonths,
} from 'date-fns';

const WEEKDAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const POPOVER_WIDTH = 280;
const POPOVER_HEIGHT_ESTIMATE = 340;
const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 4;

function computePopoverPosition(anchorRect) {
  if (!anchorRect || typeof window === 'undefined') {
    return { top: 0, left: 0 };
  }

  let left = anchorRect.left;
  let top = anchorRect.bottom + ANCHOR_GAP;

  const maxLeft = window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN;
  left = Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft));

  if (top + POPOVER_HEIGHT_ESTIMATE > window.innerHeight - VIEWPORT_MARGIN) {
    top = anchorRect.top - POPOVER_HEIGHT_ESTIMATE - ANCHOR_GAP;
  }
  top = Math.max(VIEWPORT_MARGIN, top);

  return { top, left };
}

function parseYmd(ymd) {
  if (!ymd) return null;
  const d = new Date(ymd.length === 10 ? `${ymd}T12:00:00` : ymd);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toYmd(d) {
  if (!d) return '';
  try {
    return format(d, 'yyyy-MM-dd');
  } catch {
    return '';
  }
}

/**
 * Themed single-date calendar popover — matches EventsTimeFilter dark styling.
 */
export default function DatePickerPopover({
  open,
  anchorRect,
  value,
  onSelect,
  onClear,
  onClose,
}) {
  const popoverRef = useRef(null);
  const selected = parseYmd(value);
  const [month, setMonth] = React.useState(() => startOfMonth(selected || new Date()));

  useEffect(() => {
    if (!open) return;
    const d = parseYmd(value);
    if (d) setMonth(startOfMonth(d));
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      onClose?.();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open, onClose]);

  const monthOptions = useMemo(
    () => Array.from({ length: 12 }, (_, i) => ({
      value: i,
      label: format(new Date(2024, i, 1), 'MMMM'),
    })),
    [],
  );

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 21 }, (_, i) => {
      const y = currentYear - 10 + i;
      return { value: String(y), label: String(y) };
    });
  }, []);

  const monthDropdownOptions = useMemo(
    () => monthOptions.map((opt) => ({ value: String(opt.value), label: opt.label })),
    [monthOptions],
  );

  if (!open || !anchorRect || typeof document === 'undefined') return null;

  const { top, left } = computePopoverPosition(anchorRect);

  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const leadingEmpty = (monthStart.getDay() + 6) % 7;

  const handleDayClick = (day) => {
    const ymd = toYmd(day);
    if (ymd) onSelect?.(ymd);
  };

  const handleToday = () => {
    const ymd = toYmd(new Date());
    if (ymd) onSelect?.(ymd);
  };

  return createPortal(
    <div
      ref={popoverRef}
      className="fixed z-[100000] w-[280px] rounded-lg border border-[color:var(--border-default)] bg-surface shadow-xl"
      style={{
        top,
        left,
        fontFamily: 'Inter, sans-serif',
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="p-3 select-none">
        <div className="relative z-20 flex items-center justify-between mb-2">
          <button
            type="button"
            onClick={() => setMonth(subMonths(month, 1))}
            className="p-1 rounded-lg text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-1 min-w-0">
            <BrandDropdown
              compact
              searchable={false}
              value={String(getMonth(month))}
              onChange={(v) => setMonth(setMonthDate(month, parseInt(v, 10)))}
              options={monthDropdownOptions}
              menuMinWidth={130}
              maxHeight={240}
              triggerClassName="!min-w-0"
            />
            <BrandDropdown
              compact
              searchable={false}
              value={String(getYear(month))}
              onChange={(v) => setMonth(setYearDate(month, parseInt(v, 10)))}
              options={yearOptions}
              menuMinWidth={88}
              maxHeight={200}
              triggerClassName="!min-w-0"
            />
          </div>
          <button
            type="button"
            onClick={() => setMonth(addMonths(month, 1))}
            className="p-1 rounded-lg text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-0.5 text-xs text-[color:var(--text-muted)] mb-1">
          {WEEKDAY_LABELS.map((w) => (
            <div key={w} className="text-center py-0.5">{w}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-0.5">
          {Array.from({ length: leadingEmpty }).map((_, idx) => (
            <div key={`empty-${idx}`} className="h-8" />
          ))}
          {days.map((day) => {
            const isSelected = selected && isSameDay(day, selected);
            return (
              <button
                key={day.toISOString()}
                type="button"
                onClick={() => handleDayClick(day)}
                className={`h-8 w-8 flex items-center justify-center text-sm rounded-lg transition-colors ${
                  isSelected
                    ? 'bg-[color:var(--trodo-primary)] text-ink-onBrand font-medium'
                    : 'text-[color:var(--text-primary)] hover:bg-[color:var(--surface-sunken)]'
                }`}
                aria-label={format(day, 'yyyy-MM-dd')}
                aria-pressed={isSelected}
              >
                {format(day, 'd')}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-[color:var(--border-default)] px-3 py-2">
        <button
          type="button"
          onClick={() => {
            onClear?.();
            onClose?.();
          }}
          className="text-xs font-medium text-[color:var(--trodo-primary)] hover:opacity-80 transition-opacity"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={() => {
            handleToday();
            onClose?.();
          }}
          className="text-xs font-medium text-[color:var(--trodo-primary)] hover:opacity-80 transition-opacity"
        >
          Today
        </button>
      </div>
    </div>,
    document.body,
  );
}
